import { BASE_URI } from '../settings';
import { submitJobForm } from '../core/jobSubmission';
import { createCancelOnReentry } from '../core/util';
import {
  fetchJson,
  fetchText,
  SET_FETCH_ERROR,
  withRetryKey,
} from './FetchActions';

// The Sei overview and its nested heatmap mount together, so each needs an
// independent cancellation stream. Heatmap request types still supersede one
// another as they did before.
const cancelHeatmapRequestOnReentry = createCancelOnReentry();
// A predictions-only refresh (browser window moved) can overlap the combined
// features-and-predictions load; on its own channel it cannot abort that load.
const cancelHeatmapRefreshOnReentry = createCancelOnReentry();
const cancelSeiBrowserRequestOnReentry = createCancelOnReentry();
// The two heatmap channels write the same predictions, so per retry key the
// newest attempt across both is the only one allowed to settle them. The Sei
// overview keeps its own key and is not superseded by the heatmap beside it.
const latestHeatmapAttempts = new Map();

// Only the newest request for each of these may report a failure or apply a
// result; anything older that settles later says nothing.
let latestInsilicoFailureOwner;
let latestScoreListFailureOwner;
let latestScoresFailureOwner;

/*
 * DeepSEA action types
 */
export const RECEIVE_DEEPSEA_FEATURES = 'RECEIVE_DEEPSEA_FEATURES';
export const RECEIVE_DEEPSEA_FEATURE_PREDICTIONS =
  'RECEIVE_DEEPSEA_FEATURE_PREDICTIONS';
export const RECEIVE_DEEPSEA_FEATURES_AND_PREDICTIONS =
  'RECEIVE_DEEPSEA_FEATURES_AND_PREDICTIONS';

export const RECEIVE_DEEPSEA_VARIANT_SCORES = 'RECEIVE_DEEPSEA_VARIANT_SCORES';

export const RECEIVE_DEEPSEA_JOB = 'RECEIVE_DEEPSEA_JOB';
export const RECEIVE_DEEPSEA_JOB_FEATURE_PREDICTIONS =
  'RECEIVE_DEEPSEA_JOB_FEATURE_PREDICTIONS';

export const RECEIVE_DEEPSEA_SCORE_LIST = 'RECEIVE_DEEPSEA_SCORE_LIST';

export const SET_SELECTED_DEEPSEA_JOB = 'SET_SELECTED_DEEPSEA_JOB';
export const SET_DEEPSEA_JOB_VALIDATION_ERRORS =
  'SET_DEEPSEA_JOB_VALIDATION_ERRORS';
export const SET_INSILICO_FEATURE_DATA = 'SET_INSILICO_FEATURE_DATA';
export const SET_SELECTED_INSILICO_DATA = 'SET_SELECTED_INSILICO_DATA';
export const SET_SELECTED_DEEPSEA_VARIANT_SCORE_TYPE =
  'SET_SELECTED_DEEPSEA_VARIANT_SCORE_TYPE';
export const SET_SELECTED_DEEPSEA_FEATURE_SCORE_TYPE =
  'SET_SELECTED_DEEPSEA_FEATURE_SCORE_TYPE';
export const REQUEST_INSILICO_FEATURE_DATA = 'REQUEST_INSILICO_FEATURE_DATA';
export const REQUEST_DEEPSEA_FEATURE_PREDICTIONS =
  'REQUEST_DEEPSEA_FEATURE_PREDICTIONS';
export const RESET_DEEPSEA_STATE = 'RESET_DEEPSEA_STATE';
export const SET_DEEPSEA_JOBS = 'SET_DEEPSEA_JOBS';

/*
 * Insilico action creators
 */
export function setSelectedInsilicoData(formData) {
  const { sequence_name: sequenceName, feature_name: featureName } = formData;
  return {
    type: SET_SELECTED_INSILICO_DATA,
    sequenceName,
    featureName,
  };
}

export function setInsilicoFeatureData(formData, featureData) {
  const { sequence_name: sequenceName, feature_name: featureName } = formData;
  const insilicoData = { featureName, sequenceName, featureData };
  return {
    type: SET_INSILICO_FEATURE_DATA,
    ...insilicoData,
  };
}

export function requestInsilicoFeatureData(isFetching = true) {
  return {
    type: REQUEST_INSILICO_FEATURE_DATA,
    isFetching,
  };
}

export function requestDeepSEAFeaturePredictions(
  isFetchingFeaturePredictions = true,
) {
  return {
    type: REQUEST_DEEPSEA_FEATURE_PREDICTIONS,
    isFetchingFeaturePredictions,
  };
}

export function fetchInsilicoData(jobId, dsFormData) {
  return async (dispatch, getState, { fetch }) => {
    const failureOwner = {};
    latestInsilicoFailureOwner = failureOwner;
    // A superseded attempt must not raise its banner over a newer result.
    const report = withRetryKey(action => {
      if (
        action.type !== SET_FETCH_ERROR ||
        latestInsilicoFailureOwner === failureOwner
      )
        dispatch(action);
    }, 'in-silico');
    dispatch(requestInsilicoFeatureData());
    const qs = new URLSearchParams(dsFormData);
    const url = `${BASE_URI}deepsea/jobs/${jobId}/in_silico_mutagenesis/?${qs}`;
    const featureData = await fetchJson(fetch, report, url);
    if (featureData === undefined) {
      if (latestInsilicoFailureOwner === failureOwner) {
        dispatch(requestInsilicoFeatureData(false));
        if (latestInsilicoFailureOwner === failureOwner)
          latestInsilicoFailureOwner = undefined;
      }
      return;
    }
    // A superseded attempt owns its success no more than its failure.
    if (latestInsilicoFailureOwner !== failureOwner) return;
    dispatch(setInsilicoFeatureData(dsFormData, featureData));
    dispatch(setSelectedInsilicoData(dsFormData));
    if (latestInsilicoFailureOwner === failureOwner)
      latestInsilicoFailureOwner = undefined;
  };
}

/*
 * DeepSEA Job action creators
 */
export function setSelectedDeepSEAJob(job) {
  return {
    type: SET_SELECTED_DEEPSEA_JOB,
    job,
  };
}

export function receiveDeepSEAJob(job) {
  return {
    type: RECEIVE_DEEPSEA_JOB,
    job,
  };
}

export function setDeepSEAJobValidationErrors(errors) {
  return {
    type: SET_DEEPSEA_JOB_VALIDATION_ERRORS,
    errors,
  };
}

export function fetchNewDeepSEAJob(dsFormData) {
  return async (dispatch, getState, { fetch }) => {
    const formData = new FormData();
    Object.keys(dsFormData).forEach(key => {
      // Skip null/undefined: FormData.append coerces non-Blob values
      // to strings, so a `null` upload_file (textarea path) would be
      // sent as the literal string "null". Today Django ignores it
      // because request.FILES separates files from form data, but
      // relying on that is fragile.
      if (dsFormData[key] != null) {
        formData.append(key, dsFormData[key]);
      }
    });
    // JSON-parse tolerance, network/status fallbacks, and a guaranteed
    // renderable error message all live in submitJobForm.
    const result = await submitJobForm(
      `${BASE_URI}deepsea/jobs/`,
      formData,
      fetch,
    );
    // Presence of job_id is the only reliable success signal (a 2xx can
    // still lack it). DRF returns field-level errors as {"<field>": [...]}
    // without a non_field_errors wrapper, so gating on job_id — not on the
    // absence of errors — keeps a field-error body off the success path.
    if (result.body.job_id) {
      const job = {};
      job[result.body.job_id] = result.body;
      dispatch(receiveDeepSEAJob(job));
      // Select the newly created job.
      dispatch(setSelectedDeepSEAJob(result.body));
    } else {
      // A 2xx without a job_id (a body that failed to parse, or one lacking
      // an id) leaves result.errors empty; supply a generic message so the
      // form never re-enables silently. Mirrors GwasForm's same guard.
      dispatch(
        setDeepSEAJobValidationErrors(
          Object.keys(result.errors).length > 0
            ? result.errors
            : { fetchError: 'There was an unknown error creating the job.' },
        ),
      );
    }
    // Truthy on success (job_id from a 201), falsy on every error path — the
    // form uses this to decide whether to resetForm().
    return result.body.job_id;
  };
}

export function fetchDeepSEAJobStatus(jobId) {
  const job = {};
  return async (dispatch, getState, { fetch }) => {
    const wasSaved = Boolean(getState().deepsea.jobs[jobId]);
    const url = `${BASE_URI}deepsea/jobs/${jobId}/`;
    let response;
    try {
      response = await fetch(url);
    } catch (error) {
      // A dropped connection says nothing about the job. Recording an error
      // status here would overwrite the stored job with a stub and stop the
      // poller; leaving it alone lets the next tick recover.
      return false;
    }

    if (!response.ok && response.status === 404) {
      job[jobId] = { status: 'error: job not found' };
    } else if (!response.ok) {
      // HTTP failures other than a missing job can recover on the next tick.
      return false;
    } else {
      try {
        job[jobId] = await response.json();
      } catch (error) {
        // Same reasoning as a dropped connection: keep what is stored.
        return false;
      }
    }
    // Clear must win over pending polls; a job opened by link can still join the saved list.
    if (wasSaved && !getState().deepsea.jobs[jobId]) return false;
    dispatch(receiveDeepSEAJob(job));
    return true;
  };
}

/*
 * DeepSEA action creators
 */
export function setSelectedVariantScoreType(selectedVariantScoreType) {
  return {
    type: SET_SELECTED_DEEPSEA_VARIANT_SCORE_TYPE,
    selectedVariantScoreType,
  };
}

export function setSelectedFeatureScoreType(selectedFeatureScoreType) {
  return {
    type: SET_SELECTED_DEEPSEA_FEATURE_SCORE_TYPE,
    selectedFeatureScoreType,
  };
}

export function receiveDeepSEAFeatures(features) {
  return {
    type: RECEIVE_DEEPSEA_FEATURES,
    features,
  };
}

export function receiveDeepSEAFeaturePredictions(featurePredictions) {
  return {
    type: RECEIVE_DEEPSEA_FEATURE_PREDICTIONS,
    featurePredictions,
    isFetchingFeaturePredictions: false,
  };
}

export function receiveDeepSEAFeaturesAndPredictions(
  features,
  featurePredictions,
  featureQuerySuccess,
) {
  return {
    type: RECEIVE_DEEPSEA_FEATURES_AND_PREDICTIONS,
    features,
    featurePredictions,
    featureQuerySuccess,
    isFetchingFeaturePredictions: false,
  };
}

export function receiveDeepSEAVariantScores(variantScores, failed = false) {
  return {
    type: RECEIVE_DEEPSEA_VARIANT_SCORES,
    variantScores,
    ...(failed ? { failed } : {}),
  };
}

export function receiveDeepSEAJobFeaturePredictions(featurePredictions) {
  return {
    type: RECEIVE_DEEPSEA_JOB_FEATURE_PREDICTIONS,
    featurePredictions,
    isFetchingFeaturePredictions: false,
  };
}

export function receiveDeepSEAScoreList(scoreList) {
  return {
    type: RECEIVE_DEEPSEA_SCORE_LIST,
    scoreList,
  };
}

export function resetDeepSEAState() {
  return {
    type: RESET_DEEPSEA_STATE,
  };
}

export function setDeepSEAJobs(jobs) {
  return {
    type: SET_DEEPSEA_JOBS,
    jobs,
  };
}

export function fetchDeepSEAFeatures(modelName) {
  return async (dispatch, getState, { fetch }) => {
    const json = await fetchJson(
      fetch,
      dispatch,
      `${BASE_URI}deepsea/${modelName}/features/`,
    );
    if (json === undefined) return;
    dispatch(receiveDeepSEAFeatures(json));
  };
}

export function fetchDeepSEAFeaturePredictions(modelName, params) {
  return async (dispatch, getstate, { fetch }) => {
    return cancelHeatmapRefreshOnReentry(async signal => {
      const attempt = {};
      latestHeatmapAttempts.set('heatmap', attempt);
      const settle = action => {
        if (latestHeatmapAttempts.get('heatmap') === attempt) dispatch(action);
      };
      // A response that settles as this attempt is superseded stays silent.
      const report = withRetryKey(action => {
        if (
          action.type === SET_FETCH_ERROR &&
          (signal.aborted || latestHeatmapAttempts.get('heatmap') !== attempt)
        )
          return;
        dispatch(action);
      }, 'heatmap');
      dispatch(requestDeepSEAFeaturePredictions());
      const queryStr = new URLSearchParams(params).toString();
      const featurePredictions = await fetchJson(
        fetch,
        report,
        `${BASE_URI}deepsea/${modelName}/feature_predictions/?${queryStr}`,
        { signal },
      );
      if (signal.aborted) return;
      if (featurePredictions === undefined) {
        // Clearing the predictions matters as much as stopping the spinner:
        // otherwise the previous query's heatmap stays up under the new controls.
        settle(receiveDeepSEAFeaturePredictions([]));
        return;
      }
      settle(receiveDeepSEAFeaturePredictions(featurePredictions));
    });
  };
}

function fetchFeaturesAndPredictions(
  cancelOnReentry,
  retryKey,
  modelName,
  params,
) {
  return async (dispatch, getstate, { fetch }) => {
    if (!modelName) return undefined;
    return cancelOnReentry(async signal => {
      const attempt = {};
      latestHeatmapAttempts.set(retryKey, attempt);
      const settle = action => {
        if (latestHeatmapAttempts.get(retryKey) === attempt) dispatch(action);
      };
      // A response that settles as this attempt is superseded stays silent.
      const report = withRetryKey(action => {
        if (
          action.type === SET_FETCH_ERROR &&
          (signal.aborted || latestHeatmapAttempts.get(retryKey) !== attempt)
        )
          return;
        dispatch(action);
      }, retryKey);
      dispatch(requestDeepSEAFeaturePredictions());

      const { expecto, ...predictionParams } = params;
      const predictionQueryStr = Object.keys(predictionParams)
        .map(param => `${param}=${params[param]}`)
        .join('&');

      const featureParams = new URLSearchParams();
      if (params.expecto) featureParams.append('expecto', true);
      if (params.score_type)
        featureParams.append('score_type', params.score_type);
      const featureQueryString = featureParams.toString();

      const features = await fetchJson(
        fetch,
        report,
        `${BASE_URI}deepsea/${modelName}/features/?${featureQueryString}`,
        { signal },
      );
      if (signal.aborted) return;
      if (features === undefined) {
        // Both values belong to one query; clear the previous heatmap together.
        settle(
          receiveDeepSEAFeaturesAndPredictions(
            { model_features: [], expecto_features: [] },
            [],
            false,
          ),
        );
        return;
      }

      if (latestHeatmapAttempts.get(retryKey) !== attempt) {
        // Superseded for predictions, but still the only source of features;
        // no point requesting predictions nobody will show.
        dispatch(receiveDeepSEAFeatures(features));
        return;
      }

      const featurePredictions = await fetchJson(
        fetch,
        report,
        `${BASE_URI}deepsea/${modelName}/feature_predictions/?${predictionQueryStr}`,
        { signal },
      );
      if (signal.aborted) return;
      if (featurePredictions === undefined) {
        settle(receiveDeepSEAFeaturesAndPredictions(features, [], false));
        return;
      }
      if (latestHeatmapAttempts.get(retryKey) === attempt) {
        dispatch(
          receiveDeepSEAFeaturesAndPredictions(
            features,
            featurePredictions,
            true,
          ),
        );
      } else {
        // Superseded for predictions, but still the only source of features.
        dispatch(receiveDeepSEAFeatures(features));
      }
    });
  };
}

export function fetchDeepSEAFeaturesAndPredictions(modelName, params) {
  return fetchFeaturesAndPredictions(
    cancelHeatmapRequestOnReentry,
    'heatmap',
    modelName,
    params,
  );
}

export function fetchSeiBrowserData() {
  return fetchFeaturesAndPredictions(
    cancelSeiBrowserRequestOnReentry,
    'sei-browser',
    'sei_hgmd',
    {},
  );
}

export function fetchDeepSEAScoreList(job) {
  return async (dispatch, getstate, { fetch }) => {
    const failureOwner = {};
    latestScoreListFailureOwner = failureOwner;
    const scores = await fetchJson(
      fetch,
      action => {
        if (latestScoreListFailureOwner === failureOwner) dispatch(action);
      },
      `${BASE_URI}deepsea/jobs/${job}/score_types`,
    );
    if (scores === undefined) {
      if (latestScoreListFailureOwner === failureOwner) {
        // Without a score list there are no results; say so rather than load forever.
        dispatch(receiveDeepSEAVariantScores({}, true));
        latestScoreListFailureOwner = undefined;
      }
      return;
    }
    if (latestScoreListFailureOwner !== failureOwner) return;
    dispatch(receiveDeepSEAScoreList(scores));
    if (latestScoreListFailureOwner === failureOwner)
      latestScoreListFailureOwner = undefined;
  };
}

export function fetchDeepSEAScores(scoreTypes, job) {
  return async (dispatch, getState, { fetch }) => {
    const failureOwner = {};
    latestScoresFailureOwner = failureOwner;
    const dispatchOwnedFailure = action => {
      if (latestScoresFailureOwner === failureOwner) dispatch(action);
    };
    const variantScores = {};
    const promises = [];
    scoreTypes.forEach(scoreType => {
      let scoreLevel = 'variant_scores';
      if (scoreType.level === 'SEQUENCE') scoreLevel = 'sequence_scores';

      const promise = fetchJson(
        fetch,
        dispatchOwnedFailure,
        `${BASE_URI}deepsea/jobs/${job}/${scoreLevel}/?score_type=${scoreType.short_name}`,
      );
      promises.push(promise);
    });
    const scores = await Promise.all(promises);
    // One failed score type makes the job's results incomplete; showing the
    // rest would present them as the whole picture.
    if (scores.some(score => score === undefined)) {
      dispatchOwnedFailure(receiveDeepSEAVariantScores({}, true));
    } else if (latestScoresFailureOwner === failureOwner) {
      // Only the newest fan-out may install results; an older job's success
      // arriving late would otherwise undo a newer failure.
      scores.forEach(score => {
        variantScores[score.score_type] = score;
      });
      dispatch(receiveDeepSEAVariantScores(variantScores));
    }
    if (latestScoresFailureOwner === failureOwner)
      latestScoresFailureOwner = undefined;
  };
}

export function fetchDeepSEAJobFeaturePredictions(jobName, params) {
  return async (dispatch, getstate, { fetch }) => {
    return cancelHeatmapRefreshOnReentry(async signal => {
      const attempt = {};
      latestHeatmapAttempts.set('heatmap', attempt);
      const settle = action => {
        if (latestHeatmapAttempts.get('heatmap') === attempt) dispatch(action);
      };
      // A response that settles as this attempt is superseded stays silent.
      const report = withRetryKey(action => {
        if (
          action.type === SET_FETCH_ERROR &&
          (signal.aborted || latestHeatmapAttempts.get('heatmap') !== attempt)
        )
          return;
        dispatch(action);
      }, 'heatmap');
      dispatch(requestDeepSEAFeaturePredictions());
      const queryStr = new URLSearchParams(params).toString();
      const featurePredictions = await fetchJson(
        fetch,
        report,
        `${BASE_URI}deepsea/jobs/${jobName}/feature_predictions/?${queryStr}`,
        { signal },
      );
      if (signal.aborted) return;
      if (featurePredictions === undefined) {
        // A failed refresh must not leave the previous sequence's heatmap visible.
        settle(receiveDeepSEAJobFeaturePredictions([]));
        return;
      }
      settle(receiveDeepSEAJobFeaturePredictions(featurePredictions));
    });
  };
}

export function fetchDeepSEAJobFeaturesAndPredictions(
  jobName,
  modelName,
  params,
) {
  return async (dispatch, getState, { fetch }) => {
    return cancelHeatmapRequestOnReentry(async signal => {
      const attempt = {};
      latestHeatmapAttempts.set('heatmap', attempt);
      const settle = action => {
        if (latestHeatmapAttempts.get('heatmap') === attempt) dispatch(action);
      };
      // A response that settles as this attempt is superseded stays silent.
      const report = withRetryKey(action => {
        if (
          action.type === SET_FETCH_ERROR &&
          (signal.aborted || latestHeatmapAttempts.get('heatmap') !== attempt)
        )
          return;
        dispatch(action);
      }, 'heatmap');
      dispatch(requestDeepSEAFeaturePredictions());

      const { expecto, ...predictionParams } = params;
      const predictionQueryStr = new URLSearchParams(
        predictionParams,
      ).toString();

      const featureParams = new URLSearchParams();
      if (params.expecto) featureParams.append('expecto', true);
      if (params.score_type)
        featureParams.append('score_type', params.score_type);
      const featureQueryString = featureParams.toString();

      const features = await fetchJson(
        fetch,
        report,
        `${BASE_URI}deepsea/${modelName}/features/?${featureQueryString}`,
        { signal },
      );
      if (signal.aborted) return;
      if (features === undefined) {
        // A failed feature list invalidates both sides of the paired query.
        settle(
          receiveDeepSEAFeaturesAndPredictions(
            { model_features: [], expecto_features: [] },
            [],
            false,
          ),
        );
        return;
      }
      if (latestHeatmapAttempts.get('heatmap') !== attempt) {
        // Superseded for predictions, but still the only source of features;
        // no point requesting predictions nobody will show.
        dispatch(receiveDeepSEAFeatures(features));
        return;
      }

      const featurePredictions = await fetchJson(
        fetch,
        report,
        `${BASE_URI}deepsea/jobs/${jobName}/feature_predictions/?${predictionQueryStr}`,
        { signal },
      );
      if (signal.aborted) return;
      if (featurePredictions === undefined) {
        settle(receiveDeepSEAFeaturesAndPredictions(features, [], false));
        return;
      }
      if (latestHeatmapAttempts.get('heatmap') === attempt) {
        dispatch(
          receiveDeepSEAFeaturesAndPredictions(features, featurePredictions),
        );
      } else {
        // Superseded for predictions, but still the only source of features.
        dispatch(receiveDeepSEAFeatures(features));
      }
    });
  };
}

export function fetchDeepSEAJobSubmittedVariants() {
  return async (dispatch, getState, { fetch }) => {
    const { deepsea } = getState();
    return fetchText(
      fetch,
      dispatch,
      deepsea.selectedJob.files.uploaded_query.url,
    );
  };
}
